import { useLearningUiTranslation } from '@features/learning/shared/use-learning-ui-translation'
import { lazy, Suspense, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from '@shared/ui/notification-context'
import { AppLoadingState } from '@shared/ui'
import { FlashcardsStateProvider } from './state/FlashcardsStateProvider'
import { useFlashcardsState } from './state/useFlashcardsState'
import { FlashcardCategoryScreen } from './catalog/FlashcardCategoryScreen'
import { useStudyTimeTracker } from '@features/learning/shared/useStudyTimeTracker'
import { getFlashcardCategoriesQueryOptions } from './shared/flashcard-query'
import './flashcards.css'

const LazyFlashcardCollectionScreen = lazy(() =>
  import('./collection/FlashcardCollectionScreen').then((module) => ({
    default: module.FlashcardCollectionScreen,
  })),
)

function FlashcardsFeatureShell() {
  const ui = useLearningUiTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const { actions, state } = useFlashcardsState()
  useStudyTimeTracker({ surface: 'flashcards' })
  const categoriesQuery = useQuery({
    ...getFlashcardCategoriesQueryOptions(),
    enabled: !state.categoriesLoaded,
  })

  useEffect(() => {
    if (!categoriesQuery.data || state.categoriesLoaded) {
      return
    }

    actions.setErrorMessage(null)
    actions.setCategories(categoriesQuery.data)
  }, [actions, categoriesQuery.data, state.categoriesLoaded])

  useEffect(() => {
    if (state.categoriesLoaded) {
      return
    }

    const isLoading = categoriesQuery.isLoading || categoriesQuery.isFetching

    actions.setCategoriesLoading(isLoading)

    if (isLoading) {
      actions.setErrorMessage(null)
    }
  }, [
    actions,
    categoriesQuery.isFetching,
    categoriesQuery.isLoading,
    state.categoriesLoaded,
  ])

  useEffect(() => {
    if (!categoriesQuery.error) {
      return
    }

    const message =
      categoriesQuery.error instanceof Error
        ? categoriesQuery.error.message
        : ui("Flashcard categories failed to load. Please try again.")

    actions.setErrorMessage(message)
    actions.setCategoriesLoading(false)
    enqueueSnackbar(message, { variant: 'error' })
  }, [actions, categoriesQuery.error, enqueueSnackbar, ui])

  return state.activeCategory ? (
    <Suspense
      fallback={
        <AppLoadingState
          message={ui("Keep this page open while we prepare the selected category.")}
          minHeight={220}
          title={ui("Preparing flashcards")}
        />
      }
    >
      <LazyFlashcardCollectionScreen />
    </Suspense>
  ) : (
    <FlashcardCategoryScreen />
  )
}

export function FlashcardsScreen() {
  return (
    <FlashcardsStateProvider>
      <div className="flashcardsFeatureShell" data-qa="flashcards-feature-screen">
        <FlashcardsFeatureShell />
      </div>
    </FlashcardsStateProvider>
  )
}


